import { ACCOUNT_ALREADY_EXISTS_ERROR_CODE, OAUTH_ERRORS } from '@commutifi/constants/Errors'
import { History } from 'history'
import { isEqual } from 'lodash'
import React from 'react'
import { useMutation } from '@tanstack/react-query'
import { useDispatch, useSelector } from 'react-redux'
import { Optional } from 'utility-types'
import { saveAuthenticated } from 'api/index'
import { REF_KEYS_LIST } from 'constants/onboarding'
import { OptionalFrequency } from 'shared/interfaces'
import { AnonymousRegisterFormPayload, IDPRegisterFormPayload, RegisterFormPayload } from 'shared/interfaces/Account'
import { EnterpriseSurveyPages } from 'src/routing'
import { selectors as surveySelectors } from 'store/modules/enterpriseSurvey'
import { createSurveyAnswers, resetSurveyAnswers } from 'store/modules/enterpriseSurvey/sagas'
import { wrapActionInPromise } from 'utils/helpers'
import { getCommutifiErrorCode, getHttpStatusFromError } from 'utils/index'
import { logger } from 'utils/logRocket'
import { fetchAccountKCI } from 'api/modules/accounts'

type ProcessSurveyFunc = (
  account: RegisterFormPayload | IDPRegisterFormPayload | AnonymousRegisterFormPayload
) => Promise<void>
export enum ProcessSurveyErrorIds {
  Duplicated = 'error.duplicate',
  TryAgain = 'error.tryAgain',
  OpenIdPermissions = 'onboarding.oauth.errors.openId.permission'
}

interface ProcessSurveyProps {
  processSurvey: ProcessSurveyFunc
  /**
   * Same than processSurvey but doesn't require an account payload
   * since we don't need to create or set any properties of the account
   */
  processFollowUp: () => Promise<void>
  loading: boolean
  error: any
  resetAnswers: () => void
}

export type ProcessSurveyResponse = Optional<Awaited<ReturnType<typeof fetchAccountKCI>>> & { accountId?: string }
export type SurveySuccessFunc = (
  response: ProcessSurveyResponse | undefined,
  context: {
    commuteFrequency: number | null | undefined
    remoteFrequency: number | null | undefined
    isDirectOnboarding: boolean
  }
) => void
export type SurveyErrorFunc = (errorCode: number, errorMessageId?: `${ProcessSurveyErrorIds}`) => void
interface ProcessSurveyOptions {
  onSuccess?: SurveySuccessFunc
  onError?: SurveyErrorFunc
}

const OPEN_ID_ERROR = OAUTH_ERRORS.OPENID_PERMISSION as const
const stayErrorCodes = [ACCOUNT_ALREADY_EXISTS_ERROR_CODE, OPEN_ID_ERROR] as const
const errorMessages: Record<(typeof stayErrorCodes)[number], `${ProcessSurveyErrorIds}` | undefined> = {
  [ACCOUNT_ALREADY_EXISTS_ERROR_CODE]: ProcessSurveyErrorIds.Duplicated,
  [OAUTH_ERRORS.OPENID_PERMISSION]: ProcessSurveyErrorIds.OpenIdPermissions
}
const isStayErrorCode = (errorCode: number): errorCode is (typeof stayErrorCodes)[number] =>
  stayErrorCodes.findIndex((code) => errorCode === code) >= 0

/**
 * Hook helper to reuse process survey redux actions to post the information to our APi
 * and reset the survey redux answers when it's a success or failure.
 *
 * NOTES:
 *  - Handles react-router redirect side effects
 *  - Handles redux survey side effects when creating answers with account information
 *
 * @param surveyId - Id of the survey to submit information to
 * @returns Function to process account, loading boolean and error with id
 */
export const useProcessSurvey = (
  surveyId: string,
  { onSuccess, onError }: ProcessSurveyOptions = {}
): ProcessSurveyProps => {
  const isDirectOnboarding: boolean = useSelector(surveySelectors.getIsDirectOnboarding)
  const pickWeeklyFrequency = React.useCallback((freq: OptionalFrequency) => freq?.weeklyFrequency, [])
  const commuteFrequency = useSelector(
    surveySelectors.makeGetAnswer(REF_KEYS_LIST.commuteProfileFrequency, pickWeeklyFrequency),
    isEqual
  )
  const remoteFrequency = useSelector(
    surveySelectors.makeGetAnswer(REF_KEYS_LIST.commuteProfileRemoteFrequency, pickWeeklyFrequency),
    isEqual
  )
  const dispatch = useDispatch()
  const dispatchCreateSurveyAnswers = React.useCallback(
    (...args: Parameters<typeof createSurveyAnswers>) => dispatch(createSurveyAnswers(...args)),
    [dispatch]
  )
  const dispatchResetSurveyAnswers = React.useCallback(() => dispatch(resetSurveyAnswers()), [dispatch])

  const {
    isPending: isLoading,
    error,
    mutateAsync: postSurvey
  } = useMutation<{ response: ProcessSurveyResponse }, any, Parameters<ProcessSurveyFunc>[0] | undefined>({
    mutationFn: async (account) => wrapActionInPromise(dispatchCreateSurveyAnswers, surveyId, account)
  })

  const processSurvey = React.useCallback(
    async (account: Parameters<ProcessSurveyFunc>[0] | undefined) => {
      try {
        const { response } = await postSurvey(account)

        onSuccess && onSuccess(response, { commuteFrequency, remoteFrequency, isDirectOnboarding })
      } catch (err) {
        logger.error('Failed POST survey: ', err)
        const isReduxError = (e: any): e is { payload: unknown; error: any } => typeof e === 'object' && 'payload' in e
        const unwrappedError = isReduxError(err) ? err.error : err
        const errorCode = getCommutifiErrorCode(unwrappedError) || getHttpStatusFromError(unwrappedError)

        if (isStayErrorCode(errorCode)) {
          const accountError = errorMessages[errorCode]
          if (accountError) {
            onError && onError(errorCode, accountError)
          }

          return
        }

        onError && onError(errorCode)
      }
    },
    [commuteFrequency, isDirectOnboarding, onError, onSuccess, postSurvey, remoteFrequency]
  )
  const processFollowUp = React.useCallback(() => processSurvey(undefined), [processSurvey])

  return { processSurvey, loading: isLoading, error, resetAnswers: dispatchResetSurveyAnswers, processFollowUp }
}

export const defaultProcessSurveySuccess =
  (history: History, origin: string): SurveySuccessFunc =>
  (response, context) => {
    if (response?.accountId && context.isDirectOnboarding) {
      // Redirect to dashboard
      saveAuthenticated(response.accountId, { stayLoggedIn: true })
      history.push(`/accounts/${response.accountId}`)
    } else {
      // Redirect to success screen
      history.replace({
        pathname: EnterpriseSurveyPages.Success,
        state: {
          origin,
          data: {
            ...response,
            commuteFrequency: 2 * ((context.commuteFrequency || 5) - (context.remoteFrequency || 0))
          }
        }
      })
    }
  }

export const defaultProcessSurveyError =
  (history: History, origin: string, onError?: (messageId: `${ProcessSurveyErrorIds}`) => void): SurveyErrorFunc =>
  (errorCode, errorMessageId) => {
    if (errorMessageId) {
      onError && onError(errorMessageId)
      return
    }

    // Redirect to success screen with error
    history.replace({
      pathname: EnterpriseSurveyPages.Success,
      state: {
        origin,
        errorCode
      }
    })
  }
